import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, PRODUCTS } from "@/config/site";

export default function Products({ onBuy, images }) {
  const [stock, setStock] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/stock`)
      .then((r) => setStock(r.data))
      .catch(() => {});
  }, []);

  const remainingFor = (id) => {
    const s = stock?.find((x) => x.product_id === id);
    if (!s) return null;
    return s.packs.reduce((a, p) => a + p.remaining, 0);
  };

  const priceFor = (id) => {
    const s = stock?.find((x) => x.product_id === id);
    return { deal: s?.deal_price ?? null, orig: s?.original_price ?? null };
  };

  return (
    <section id="drop" className="relative bg-[#0A0A0A] py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-xs tracking-[0.35em] text-[#E60000] uppercase">
            Two platforms. Zero fluff.
          </p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl mt-3 text-white">
            Pick your poison
          </h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          {PRODUCTS.map((p, i) => {
            const left = remainingFor(p.id);
            const soldOut = left === 0;
            return (
              <motion.article
                key={p.id}
                data-testid={p.card_testid}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                className="group relative bg-[#121212] border border-[#262626] hover:border-[#E60000]/60 transition-colors flex flex-col"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={(images && images[p.id]) || p.image}
                    alt={p.name}
                    className="w-full h-72 object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700"
                  />
                  <span
                    data-testid={`badge-${p.id}`}
                    className="absolute top-4 left-4 bg-[#E60000] text-white font-mono text-[10px] tracking-[0.2em] px-3 py-1.5"
                  >
                    {p.badge}
                  </span>
                  {left !== null && !soldOut && (
                    <span
                      data-testid={`stock-left-${p.id}`}
                      className="absolute top-4 right-4 bg-white text-black font-mono text-[10px] tracking-[0.2em] px-3 py-1.5"
                    >
                      ONLY {left} PACK{left > 1 ? "S" : ""} LEFT
                    </span>
                  )}
                  {soldOut && (
                    <span
                      data-testid={`sold-out-${p.id}`}
                      className="absolute top-4 right-4 bg-[#E60000] text-white font-mono text-[10px] tracking-[0.2em] px-3 py-1.5"
                    >
                      SOLD OUT
                    </span>
                  )}
                  <span className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-xl border border-[#262626] font-mono text-[10px] tracking-[0.2em] px-3 py-1.5 text-[#E60000]">
                    {p.discount}
                  </span>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display uppercase text-2xl text-white">{p.name}</h3>
                  <p className="font-mono text-xs text-[#888] mt-2 leading-relaxed">
                    {p.description}
                  </p>
                  <p
                    data-testid={`pack-info-${p.id}`}
                    className="font-mono text-[11px] tracking-[0.1em] text-[#E60000] mt-3 uppercase"
                  >
                    {p.stock_text}
                  </p>
                  <div className="mt-6 flex-1">
                    <span
                      data-testid={`price-original-${p.id}`}
                      className="font-mono text-lg text-[#666] line-through mr-3"
                    >
                      ${priceFor(p.id).orig ?? p.original_price}
                    </span>
                    <span
                      data-testid={`price-deal-${p.id}`}
                      className="font-display text-5xl text-[#E60000]"
                    >
                      ${priceFor(p.id).deal ?? p.deal_price}
                    </span>
                    <p className="font-mono text-[10px] tracking-[0.2em] text-[#666] mt-2">
                      + SHIPPING NOT INCLUDED
                    </p>
                  </div>
                  <button
                    onClick={() => onBuy(p)}
                    disabled={soldOut}
                    data-testid={p.buy_testid}
                    className={`mt-6 w-full font-display uppercase tracking-wide text-lg py-4 transition-colors ${
                      soldOut
                        ? "bg-[#1a1a1a] text-[#555] cursor-not-allowed"
                        : "bg-white text-black hover:bg-[#E60000] hover:text-white"
                    }`}
                  >
                    {soldOut ? "Sold out" : "Cop now"}
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
